
import { useState } from 'react';
import { Facebook, Instagram, ThumbsDown, ThumbsUp } from 'lucide-react';

function XLogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.829L1.254 2.25H8.08l4.253 5.622L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  );
}
import { buildGuideFeedbackKey, GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH, GUIDE_XAT_USERNAME_MAX_LENGTH } from '@/lib/guide-feedback';
import { useGuideFeedback } from '@/components/help-center/guide-feedback-context';
import { cn } from '@/lib/utils';

type ArticleFeedbackSectionProps = {
  categoryId: string;
  slug: string;
  articleTitle: string;
};

export function ArticleFeedbackSection({
  categoryId,
  slug,
  articleTitle,
}: ArticleFeedbackSectionProps) {
  const { hydrated, getStats, getVisitorVote, submitLike, submitNegativeFeedback } =
    useGuideFeedback();

  const guideKey = buildGuideFeedbackKey(categoryId, slug);
  const stats = getStats(guideKey);
  const visitorVote = hydrated ? getVisitorVote(guideKey) : null;

  const [showNegativeForm, setShowNegativeForm] = useState(false);
  const [negativeMessage, setNegativeMessage] = useState('');
  const [xatUsername, setXatUsername] = useState('');
  const [negativeSubmitted, setNegativeSubmitted] = useState(false);
  const [shareNotice, setShareNotice] = useState<string | null>(null);

  const getShareUrl = () =>
    typeof window !== 'undefined' ? window.location.href : '';

  const shareText = encodeURIComponent(articleTitle);

  const totalVotes = stats.likes + stats.dislikes;
  const statsLabel =
    totalVotes === 0
      ? 'Seja o primeiro a avaliar este artigo.'
      : `${stats.likes.toLocaleString('pt-BR')} de ${totalVotes.toLocaleString('pt-BR')} pessoas acharam este artigo útil`;

  const handleLike = () => {
    if (visitorVote) return;
    submitLike(guideKey);
    setShowNegativeForm(false);
    setNegativeSubmitted(false);
  };

  const handleNeedHelpClick = () => {
    if (visitorVote === 'like') return;
    setShowNegativeForm(true);
    setNegativeSubmitted(false);
  };

  const handleNegativeSubmit = () => {
    const ok = submitNegativeFeedback({
      guideKey,
      categoryId,
      slug,
      articleTitle,
      message: negativeMessage,
      xatUsername,
    });
    if (!ok) return;
    setNegativeSubmitted(true);
    setShowNegativeForm(false);
    setNegativeMessage('');
    setXatUsername('');
  };

  const openShare = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=520');
  };

  const copyShareLink = async () => {
    const shareUrl = getShareUrl();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareNotice('Link copiado — cole no Instagram ou onde quiser compartilhar.');
    } catch {
      setShareNotice('Não foi possível copiar o link. Copie o endereço da página manualmente.');
    }
    window.setTimeout(() => setShareNotice(null), 4000);
  };

  return (
    <div className="border-t border-gray-200 bg-gray-50 px-6 py-8 sm:px-10 md:px-12">
      <div className="mb-8">
        <h3 className="mb-4 text-xl font-bold text-gray-900">Este artigo foi útil?</h3>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleLike}
            disabled={visitorVote != null}
            className={cn(
              'flex items-center gap-2 rounded-xl border-2 px-6 py-3 font-medium transition-all disabled:cursor-not-allowed disabled:opacity-70',
              visitorVote === 'like'
                ? 'border-blue-600 bg-blue-600 text-white shadow-lg'
                : 'border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
            )}
          >
            <ThumbsUp className="h-5 w-5" />
            Sim, ajudou
          </button>
          <button
            type="button"
            onClick={handleNeedHelpClick}
            disabled={visitorVote === 'like'}
            className={cn(
              'flex items-center gap-2 rounded-xl border-2 px-6 py-3 font-medium transition-all disabled:cursor-not-allowed disabled:opacity-70',
              visitorVote === 'dislike' || showNegativeForm
                ? 'border-red-600 bg-red-600 text-white shadow-lg'
                : 'border-gray-300 text-gray-700 hover:border-red-300 hover:bg-red-50'
            )}
          >
            <ThumbsDown className="h-5 w-5" />
            Preciso de mais ajuda
          </button>
        </div>

        {showNegativeForm && visitorVote !== 'dislike' && !negativeSubmitted ? (
          <div className="mt-4 rounded-2xl border border-red-200 bg-white p-4 shadow-sm sm:p-5">
            <p className="mb-2 font-poppins text-sm font-semibold text-gray-900">
              O que faltou neste tutorial?
            </p>
            <p className="mb-3 font-inter text-xs text-gray-500">
              Conte em poucas palavras o que não ficou claro — nossa equipe irá avaliar seu Feedback e correções serão feitas, se necessário.
            </p>
            <label className="mb-1.5 block font-poppins text-xs font-semibold text-gray-700">
              Username no xat
            </label>
            <input
              type="text"
              value={xatUsername}
              onChange={(e) =>
                setXatUsername(e.target.value.slice(0, GUIDE_XAT_USERNAME_MAX_LENGTH))
              }
              maxLength={GUIDE_XAT_USERNAME_MAX_LENGTH}
              placeholder="Ex.: MeuNick"
              autoComplete="username"
              className="mb-3 w-full rounded-xl border border-gray-300 px-4 py-2.5 font-inter text-sm text-gray-800 outline-none transition-colors focus:border-red-400 focus:ring-2 focus:ring-red-100"
            />
            <label className="mb-1.5 block font-poppins text-xs font-semibold text-gray-700">
              O que faltou?
            </label>
            <textarea
              value={negativeMessage}
              onChange={(e) =>
                setNegativeMessage(e.target.value.slice(0, GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH))
              }
              rows={4}
              maxLength={GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH}
              placeholder="Ex.: Faltou explicar como salvar as alterações no perfil…"
              className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 font-inter text-sm text-gray-800 outline-none transition-colors focus:border-red-400 focus:ring-2 focus:ring-red-100"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <span className="font-inter text-xs text-gray-400">
                {negativeMessage.length}/{GUIDE_NEGATIVE_FEEDBACK_MAX_LENGTH}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowNegativeForm(false);
                    setNegativeMessage('');
                    setXatUsername('');
                  }}
                  className="rounded-xl border border-gray-300 px-4 py-2 font-poppins text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleNegativeSubmit}
                  disabled={!negativeMessage.trim()}
                  className="rounded-xl bg-red-600 px-4 py-2 font-poppins text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Enviar feedback
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {negativeSubmitted || visitorVote === 'dislike' ? (
          <p className="mt-4 text-sm font-medium text-emerald-700">
            Obrigado pelo feedback. A equipe vai analisar sua mensagem.
          </p>
        ) : null}

        <p className="mt-4 text-sm font-medium text-gray-500">{hydrated ? statsLabel : '…'}</p>
      </div>

      <div className="border-t border-gray-200 pt-8">
        <h3 className="mb-4 text-lg font-bold text-gray-900">Compartilhar esse guia</h3>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              const url = encodeURIComponent(getShareUrl());
              openShare(`https://www.facebook.com/sharer/sharer.php?u=${url}`);
            }}
            className="rounded-xl border-2 border-gray-200 bg-white p-3 text-gray-600 transition-all hover:border-[rgba(0,11,106,0.35)] hover:text-[#000b6a] hover:shadow-md"
            aria-label="Compartilhar no Facebook"
          >
            <Facebook className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => {
              const url = encodeURIComponent(getShareUrl());
              openShare(`https://twitter.com/intent/tweet?url=${url}&text=${shareText}`);
            }}
            className="rounded-xl border-2 border-gray-200 bg-white p-3 text-gray-600 transition-all hover:border-[rgba(0,11,106,0.35)] hover:text-[#000b6a] hover:shadow-md"
            aria-label="Compartilhar no X"
          >
            <XLogoIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => void copyShareLink()}
            className="rounded-xl border-2 border-gray-200 bg-white p-3 text-gray-600 transition-all hover:border-pink-300 hover:text-pink-600 hover:shadow-md"
            aria-label="Copiar link para compartilhar no Instagram"
          >
            <Instagram className="h-5 w-5" />
          </button>
        </div>
        {shareNotice ? (
          <p className="mt-3 text-sm font-medium text-gray-600">{shareNotice}</p>
        ) : null}
      </div>

      <div className="mt-8 border-t border-gray-200 pt-8">
        <p className="mb-4 text-gray-700">
          Ainda tem dúvidas? Nossa equipe de suporte está aqui para ajudar.
        </p>
        <a
          href="https://xat.com/Ajuda"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white shadow-lg transition-colors hover:bg-blue-700 hover:shadow-xl"
        >
          Enviar uma solicitação
        </a>
      </div>
    </div>
  );
}
